import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from './i18n';
import './ServiceManager.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SM_T = {
  kk: {
    title: '⚙️ Қызметтерді басқару',
    active: 'Белсенді',
    of: 'қызметтің ішінен',
    info: '💡 Қосылған қызметтер тұрғындарға өтінімдер санаты ретінде көрінеді. Өшіру бұл қызмет қызметкерлерінің кіруін бұғаттайды. Ескі өтінімдер сақталады.',
    loading: 'Жүктелуде...',
    loadError: 'Қызметтер тізімін жүктеу мүмкін болмады',
    retry: 'Қайталау',
    toggleError: (name) => `«${name}» қызметін ауыстыру қатесі`,
    emergencyBadge: 'ТЖ',
    done: 'Дайын',
    close: 'Жабу'
  },
  ru: {
    title: '⚙️ Управление службами',
    active: 'Активно',
    of: 'из',
    info: '💡 Включенные службы видны жителям как категории для заявок. Отключение блокирует логин сотрудников в эту службу. Старые заявки сохраняются.',
    loading: 'Загрузка...',
    loadError: 'Не удалось загрузить список служб',
    retry: 'Повторить',
    toggleError: (name) => `Ошибка переключения службы «${name}»`,
    emergencyBadge: 'ЧС',
    done: 'Готово',
    close: 'Закрыть'
  }
};

const ServiceManager = ({ onClose }) => {
  const { language } = useLanguage();
  const t = SM_T[language];
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const loadServices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/admin/services`);
      setServices(response.data.services || []);
    } catch (err) {
      console.error('Load services error:', err);
      setError(t.loadError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleToggle = async (service) => {
    const newEnabled = !service.enabled;
    setTogglingId(service.id);
    try {
      const response = await axios.patch(
        `${API}/admin/services/${service.id}/toggle`,
        { enabled: newEnabled }
      );

      // Update local state
      setServices(prev => prev.map(s =>
        s.id === service.id ? { ...s, enabled: response.data.enabled } : s
      ));
    } catch (err) {
      console.error('Toggle error:', err);
      alert(t.toggleError(language === 'kk' ? (service.name_kk || service.name_ru) : service.name_ru));
    } finally {
      setTogglingId(null);
    }
  };

  const activeCount = services.filter(s => s.enabled).length;

  return (
    <div className="sm-overlay" data-testid="service-manager-modal" onClick={onClose}>
      <div className="sm-panel" onClick={(e) => e.stopPropagation()}>
        <div className="sm-header">
          <div>
            <h2 className="sm-title">{t.title}</h2>
            <p className="sm-subtitle">
              {t.active}: <strong>{activeCount}</strong> {t.of} {services.length}
            </p>
          </div>
          <button
            className="sm-close-btn"
            onClick={onClose}
            data-testid="service-manager-close"
            aria-label={t.close}
          >
            ✕
          </button>
        </div>

        <div className="sm-info">
          {t.info}
        </div>

        {loading && (
          <div className="sm-loading" data-testid="sm-loading">
            <div className="sm-spinner"></div>
            <p>{t.loading}</p>
          </div>
        )}

        {error && (
          <div className="sm-error" data-testid="sm-error">
            {error}
            <button onClick={loadServices} className="sm-retry-btn">{t.retry}</button>
          </div>
        )}

        {!loading && !error && (
          <div className="sm-list" data-testid="sm-services-list">
            {services.map(service => (
              <div
                key={service.id}
                className={`sm-item ${service.enabled ? 'enabled' : 'disabled'}`}
                data-testid={`sm-service-${service.id}`}
              >
                <div className="sm-item-info">
                  <span className="sm-item-icon" style={{ background: service.color + '22' }}>
                    {service.icon}
                  </span>
                  <div className="sm-item-text">
                    <div className="sm-item-name">{language === 'kk' ? (service.name_kk || service.name_ru) : service.name_ru}</div>
                    <div className="sm-item-meta">
                      <span className="sm-item-kk">{language === 'kk' ? service.name_ru : service.name_kk}</span>
                      {service.is_emergency && <span className="sm-badge sm-badge-emergency">{t.emergencyBadge}</span>}
                    </div>
                  </div>
                </div>

                <label className="sm-switch" data-testid={`sm-toggle-${service.id}`}>
                  <input
                    type="checkbox"
                    checked={service.enabled}
                    onChange={() => handleToggle(service)}
                    disabled={togglingId === service.id}
                  />
                  <span className="sm-switch-slider">
                    {togglingId === service.id && <span className="sm-mini-spinner"></span>}
                  </span>
                </label>
              </div>
            ))}
          </div>
        )}

        <div className="sm-footer">
          <button className="sm-action-btn" onClick={onClose} data-testid="sm-done-btn">
            {t.done}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceManager;
